/**
 * Multi-GCP editor - child of TransformPanel. Content is #multi-gcps inside transform popup.
 * UI logic moved from MultiGCPEditorUI. Init via initWithGCPManager when asset is ready.
 */
import type { GCPManager } from "../core/gcp/GCPManager";
import { ConstruktedTransformEditors } from "../types/common";
import { saveGeolocation } from "../construkted_ajax";
import { BasePanel } from "./BasePanel";
import type { PanelContainer } from "./BasePanel";

export class MultiGCPEditorPanel extends BasePanel {
    private _container: ReturnType<typeof window.jQuery> | null = null;

    private _gcpManager: GCPManager | null = null;

    private _discardAndRestoreTransform(gcpManager: GCPManager): void {
        window.Construkted.assetViewer.showHideSceneBackground(window.CONSTRUKTED_AJAX.terrain_imagery_enabled);
        gcpManager.cancel();
        this.getParentPanel()?.reset();
        window.panelManager?.closePanelInternal(this.getId());
    }

    getId(): string {
        return "multi-gcp-editor";
    }

    getContainer(): PanelContainer {
        if (!this._container) {
            this._container = window.jQuery("#multi-gcps");
        }

        return this._container;
    }

    /** Called by TransformPanel when opening multi-gcp editor. Sets up event handlers. */
    initWithGCPManager(gcpManager: GCPManager): void {
        this._gcpManager = gcpManager;

        const jQuery = window.jQuery;
        const assetViewer = window.Construkted.assetViewer;

        const jqAddGCPButton = jQuery("#add_gcp_button");
        const jqUpdateAssetLocationButton = jQuery("#update_asset_location_button");
        const jqUndergroundCheckBox = jQuery("#multiple-gcp-is-underground-checkbox");
        const jqSaveAndCloseButton = jQuery("#multi-gcp-editor-save-close-button");
        const jqCancelButton = jQuery("#multi-gcp-editor-cancel-button");
        const jqResetButton = jQuery("#multi-gcp-editor-reset-button");

        jqAddGCPButton.off("click").on("click", () => gcpManager._newGCPInterface());

        jqUpdateAssetLocationButton.off("click").on("click", () => {
            if (!gcpManager._checkGCP()) {
                return;
            }

            gcpManager._updateAssetLocation();
        });

        jqSaveAndCloseButton.off("click").on("click", async () => {
            if (!gcpManager.calculated) {
                alert("Please update asset location!");

                return;
            }

            const GCPJSONString = gcpManager.calculated ? gcpManager.workingResultAsJson() : undefined;
            const saveOptions = {
                activeEditor: ConstruktedTransformEditors.MultiGCPsEditor,
                asset: assetViewer.masterAsset,
                GCPJSONString: GCPJSONString,
                isUnderground: jqUndergroundCheckBox.prop("checked") as boolean,
                terrainImageryEnabled: window.CONSTRUKTED_AJAX.terrain_imagery_enabled,
                jqControls: [jqSaveAndCloseButton]
            };
            const res = await saveGeolocation(saveOptions, () => {
                gcpManager.hideAllGCPs();
                this.getParentPanel()?.reset();
            });

            if (res) {
                gcpManager.ignoreWorkingResult();
            }
        });

        jqCancelButton.off("click").on("click", () => {
            if (gcpManager.calculated) {
                if (!window.confirm("Are you sure you want to close? You will lose all changes")) {
                    return;
                }
            }

            this._discardAndRestoreTransform(gcpManager);
        });

        jqResetButton.off("click").on("click", () => gcpManager.reset());

        jqUndergroundCheckBox.prop("checked", window.CONSTRUKTED_AJAX.asset_is_underground);
        jqUndergroundCheckBox.off("change").on("change", function (this: HTMLInputElement) {
            assetViewer.enableDisableUnderground(this.checked);
        });
    }

    show(): void {
        const $ = window.jQuery;

        $("#transform-container").hide();
        $("#globe-status-display").hide();
        $("#visual").removeClass("active");
        $("#multi-gcps").addClass("active");
        this._opened = true;
        this.onShow();
    }

    hide(): void {
        window.jQuery("#multi-gcps").removeClass("active");
        this._opened = false;
        this.onHide();
    }

    hasUnsavedChanges(): boolean {
        return this.isOpen();
    }

    confirmDiscardChanges(): boolean {
        const ok = window.confirm("Are you sure you want to close? You will lose all changes");

        if (ok && this._gcpManager) {
            this._discardAndRestoreTransform(this._gcpManager);
        }

        return ok;
    }
}
